import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import numeral from 'numeral';

import AccountAx      from 'app/actions/account';
import EllipsisMenu   from 'app/components/common/ellipsis-menu';
import Icon           from 'app/components/common/icon';
import Wysiwyg        from 'app/components/common/rich-text/wysiwyg';
import StandardInput  from 'app/components/common/standard-input'
import format         from 'app/helpers/format';
import proseMirror    from 'app/helpers/rich-text/prose-mirror';
import AccountSlx     from 'app/selectors/account';
import FfSlx          from 'app/selectors/feature-flags';

const noop = () => {};
const dropdownIcon = <Icon.Cog1 />;

const mdWordCount = (md) => {
  const txt = proseMirror.mdToTxt(md || '').trim();
  if (!txt) return 0;
  return txt.split(/\s+/).length;
};

class RteEditor extends React.PureComponent {

  constructor(props) {
    super(props);

    this.refWysiwyg = React.createRef();
    this.refMdInput = React.createRef();

    this.state = {
      mode: (() => {
        const pref = props.prefs.rteMode;
        if (['wysiwyg', 'markdown'].includes(pref)) return pref;
        return 'wysiwyg';
      })(),
      md: props.initialMd,
      wordCount: mdWordCount(props.initialMd),
    };

    this.onClickMd = this.onClickMd.bind(this);
    this.onClickWysiwyg = this.onClickWysiwyg.bind(this);
    this.onChangeMd = this.onChangeMd.bind(this);
    this.onChangeWysiwyg = this.onChangeWysiwyg.bind(this);
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.focus();
    }
  }

  get isWysiwyg() {
    return this.state.mode === 'wysiwyg';
  }

  get vMsg() {
    const msg = _.get(this.props.validations, `${this.props.name}[0]`);
    if (msg) return msg;
    const wordWord = format.pluralize('word', this.maxWords);
    if (this.isOverMax) return `Must be ${numeral(this.maxWords).format('0,0')} ${wordWord} or fewer`;
    return null;
  }

  get maxWords() {
    const {maxWords} = this.props;
    if ((maxWords || 0) <= 0) return Infinity;
    return maxWords;
  }
  get maxWordsLabel() {
    const {wordCount} = this.state;
    if (!_.isFinite(this.maxWords)) return null;
    const formatNum = (num) => numeral(num).format('0,0');
    return `${formatNum(wordCount)} / ${formatNum(this.maxWords)} words`;
  }
  get isMaxed() {
    return this.state.wordCount >= this.maxWords;
  }
  get isOverMax() {
    return this.state.wordCount > this.maxWords;
  }

  // called externally
  focus() {
    this.refWysiwyg?.current?.focus();
    this.refMdInput?.current?.element?.focus();
  }

  onClickMd(event) {
    event.preventDefault();
    this.setState({
      mode: 'markdown',
      md: this.refWysiwyg.current.md,
    });
    this.props.saveMode('markdown');
  }

  onClickWysiwyg(event) {
    event.preventDefault();
    this.setState({mode: 'wysiwyg'});
    this.props.saveMode('wysiwyg');
  }

  onChangeMd(event) {
    const md = event.target.value;
    const wordCount = mdWordCount(md);
    this.setState({md, wordCount});
    this.props.onChange(md);
  }

  onChangeWysiwyg(md) {
    const wordCount = mdWordCount(md);
    this.setState({wordCount});
    this.props.onChange(md);
  }

  render() {
    const {placeholder, FfMd, name, validations, hideTextBlocks, tall, className} = this.props;
    const {md, wordCount} = this.state;

    return (
      <div className={`rte-editor ${this.vMsg ? 'invalid' : ''} ${className}`}>
        {FfMd && (
          <EllipsisMenu className="rte-editor-settings" usePortal icon={dropdownIcon}>
            {this.isWysiwyg
              ? <a href="#" onClick={this.onClickMd}>switch to markdown</a>
              : <a href="#" onClick={this.onClickWysiwyg}>switch to rich text</a>
            }
          </EllipsisMenu>
        )}
        {this.isWysiwyg
          ? <Wysiwyg initialMd={md} onChange={this.onChangeWysiwyg} placeholder={placeholder} ref={this.refWysiwyg} hideTextBlocks={hideTextBlocks} tall={tall} />
          : <StandardInput className="rte-editor-md-input" onChange={this.onChangeMd} value={md} name={name} validations={validations} label={placeholder} type="textarea" ref={this.refMdInput} />
        }
        <div className="_flex _justify-start _align-start">
          {!!(this.isWysiwyg && this.vMsg) ? (
            <div className="validation-message _flex-1">{this.vMsg}</div>
          ) : (
            <div className="_flex-1" />
          )}
          {this.maxWordsLabel && (
            <div className={`rte-editor-max-words ${this.isMaxed ? 'maxed' : ''}`}>{this.maxWordsLabel}</div>
          )}
        </div>
      </div>
    );
  }

}

RteEditor.propTypes = {
  initialMd: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  validations: PropTypes.object,
  hideTextBlocks: PropTypes.bool,
  tall: PropTypes.bool,
  maxWords: PropTypes.number,
  className: PropTypes.string,
  focusOnMount: PropTypes.bool,
};

RteEditor.defaultProps = {
  initialMd: '',
  onChange: noop,
  placeholder: '',
  name: '',
  validations: null,
  hideTextBlocks: false,
  tall: false,
  maxWords: null,
  className: '',
  focusOnMount: false,
};

const stateToProps = (state) => ({
  prefs: AccountSlx.prefs(state),
  FfMd: FfSlx['rte-md'](state),
});

const dispatchToProps = (dispatch) => ({
  saveMode: (mode) => dispatch(AccountAx.setPrefs({rteMode: mode})),
});

export default connect(stateToProps, dispatchToProps, null, {forwardRef: true})(RteEditor);
